import { Icon } from '@iconify/react';
import { Badge } from '@/components/shadcn/badge';
import { Button } from '@/components/shadcn/button';
import { Skeleton } from '@/components/shadcn/skeleton';
import type { WhatsappCommunityMessage } from '@/services/whatsappCommunityService';
import {
  formatWhatsappDatetime,
  formatWhatsappMessageStatus,
  whatsappStatusBadgeClass,
} from '@/components/community/whatsapp/whatsappUiUtils';

export type WhatsappMessageRow = WhatsappCommunityMessage & { _no: number };

interface WhatsappMessageListProps {
  messages: WhatsappMessageRow[];
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onViewMessage: (content: string) => void;
}

const WhatsappMessageList = ({
  messages,
  isLoading,
  hasMore,
  onLoadMore,
  onViewMessage,
}: WhatsappMessageListProps) => {
  if (isLoading && messages.length === 0) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
        <Icon icon="mdi:message-text-outline" className="mx-auto mb-2 h-8 w-8 text-gray-300" />
        <p className="text-sm font-medium text-gray-700">Belum ada pesan</p>
        <p className="mt-1 text-xs text-gray-500">Log pengiriman WhatsApp akan muncul di sini.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-3 lg:hidden">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className="rounded-xl border border-gray-100 bg-white p-3.5 shadow-sm"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-[#0E1726]">
                  {msg.to_number || '-'}
                </p>
                <p className="mt-0.5 text-xs text-gray-400">
                  {formatWhatsappDatetime(msg.sent_at ?? msg.created_at)}
                </p>
              </div>
              <Badge
                variant="secondary"
                className={`shrink-0 ${whatsappStatusBadgeClass(msg.status)}`}
              >
                {formatWhatsappMessageStatus(msg.status)}
              </Badge>
            </div>
            <p className="mt-2 line-clamp-2 text-sm text-[#556575]">{msg.message_content}</p>
            {(msg.status === 'failed' || msg.status === 'cancelled') && msg.error_message && (
              <p className="mt-2 line-clamp-2 text-xs text-red-600">{msg.error_message}</p>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3 h-8 rounded-lg"
              onClick={() => onViewMessage(msg.message_content || '')}
            >
              Lihat pesan
            </Button>
          </div>
        ))}
      </div>

      <div className="hidden overflow-x-auto rounded-xl border border-gray-100 lg:block">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-4 py-3">No</th>
              <th className="px-4 py-3">Ke</th>
              <th className="px-4 py-3">Pesan</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Waktu</th>
              <th className="px-4 py-3">Keterangan</th>
              <th className="px-4 py-3">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {messages.map((msg) => (
              <tr key={msg.id} className="bg-white">
                <td className="px-4 py-3 text-gray-500">{msg._no}</td>
                <td className="px-4 py-3 font-medium text-[#0E1726]">{msg.to_number}</td>
                <td className="max-w-xs truncate px-4 py-3 text-[#556575]">{msg.message_content}</td>
                <td className="px-4 py-3">
                  <Badge
                    variant="secondary"
                    className={whatsappStatusBadgeClass(msg.status)}
                  >
                    {formatWhatsappMessageStatus(msg.status)}
                  </Badge>
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-gray-500">
                  {formatWhatsappDatetime(msg.sent_at ?? msg.created_at)}
                </td>
                <td className="max-w-[200px] px-4 py-3 text-xs text-gray-500">
                  {msg.status === 'failed' || msg.status === 'cancelled'
                    ? msg.error_message || '-'
                    : '-'}
                </td>
                <td className="px-4 py-3">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-lg"
                    onClick={() => onViewMessage(msg.message_content || '')}
                  >
                    Lihat
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full rounded-xl"
          onClick={onLoadMore}
          disabled={isLoading}
        >
          Load more
        </Button>
      )}
    </div>
  );
};

export default WhatsappMessageList;
